/**
 * Aufgaben und Checklisten auf dem Gerät (E12).
 *
 * Listen sind privat (`householdId` leer) oder geteilt. Teilen zieht Liste,
 * Aufgaben und Erledigt-Einträge **in einem Schritt** in den Haushalt — der
 * Server lehnt eine geteilte Aufgabe an einer privaten Liste ab.
 *
 * Erledigen schreibt immer einen Eintrag in `task_completions` (die
 * Vergangenheit). Wiederkehrende Aufgaben rücken dabei weiter, statt erledigt
 * zu bleiben; Checklisten-Punkte werden erst beim Abschluss des Durchgangs
 * zurückgesetzt.
 */

import { isoDateInZone, newId, nextDueDate, taskState } from '@kraftwerk/shared';
import type { Repeat, TaskListKind, TaskState } from '@kraftwerk/shared';
import { db } from './local.js';
import type { KraftwerkDatabase, LocalTask, LocalTaskCompletion, LocalTaskList } from './local.js';
import { stamp, writeRow, writeRows } from './write.js';
import type { RowChange } from './write.js';

const live = <T extends { deletedAt: string | null }>(rows: T[]) =>
  rows.filter((row) => !row.deletedAt);

export async function listTaskLists(database: KraftwerkDatabase = db): Promise<LocalTaskList[]> {
  return live(await database.task_lists.toArray()).sort(
    (a, b) => a.position - b.position || a.name.localeCompare(b.name),
  );
}

export async function createTaskList(
  input: { name: string; kind: TaskListKind; householdId: string | null },
  database: KraftwerkDatabase = db,
): Promise<LocalTaskList> {
  const count = (await listTaskLists(database)).length;
  return writeRow(
    'task_lists',
    {
      id: newId(),
      householdId: input.householdId,
      name: input.name.trim(),
      kind: input.kind,
      position: count,
      updatedAt: stamp(),
      deletedAt: null,
    },
    'upsert',
    database,
  );
}

export async function renameTaskList(
  id: string,
  name: string,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const row = await database.task_lists.get(id);
  if (!row || row.deletedAt) return;
  await writeRow(
    'task_lists',
    { ...row, name: name.trim(), updatedAt: stamp() },
    'upsert',
    database,
  );
}

/**
 * Liste teilen oder wieder privat machen — mit allem, was daran hängt.
 * Privat machen kann nur, wer alle Zeilen angelegt hat (sonst lehnt der Server
 * ab); `canUnshare` sagt das vorher.
 */
export async function setListHousehold(
  listId: string,
  householdId: string | null,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const list = await database.task_lists.get(listId);
  if (!list || list.deletedAt) return;
  const now = stamp();
  const [items, done] = await Promise.all([
    database.tasks.where('listId').equals(listId).toArray(),
    database.task_completions.where('listId').equals(listId).toArray(),
  ]);
  // Liste zuerst — der Server prüft Aufgaben gegen den Haushalt ihrer Liste.
  // Beim Privatmachen umgekehrt: erst die Aufgaben, dann die Liste.
  const listChange: RowChange = {
    table: 'task_lists',
    row: { ...list, householdId, updatedAt: now },
  };
  const rest: RowChange[] = [
    // Oberaufgaben vor Unterpunkten.
    ...items
      .sort((a, b) => Number(a.parentId !== null) - Number(b.parentId !== null))
      .map((row): RowChange => ({
        table: 'tasks',
        row: {
          ...row,
          householdId,
          assigneeId: householdId ? row.assigneeId : null,
          updatedAt: now,
        },
      })),
    ...done.map((row): RowChange => ({
      table: 'task_completions',
      row: { ...row, householdId, updatedAt: now },
    })),
  ];
  await writeRows(householdId ? [listChange, ...rest] : [...rest.reverse(), listChange], database);
}

/** Privat machen geht nur, wenn alles daran von mir ist. */
export async function canUnshare(
  listId: string,
  me: string | null,
  database: KraftwerkDatabase = db,
): Promise<boolean> {
  if (!me) return false;
  const [list, items, done] = await Promise.all([
    database.task_lists.get(listId),
    database.tasks.where('listId').equals(listId).toArray(),
    database.task_completions.where('listId').equals(listId).toArray(),
  ]);
  return [list, ...items, ...done].every(
    (row) => !row || row.userId === undefined || row.userId === me,
  );
}

export async function deleteTaskList(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const list = await database.task_lists.get(id);
  if (!list || list.deletedAt) return;
  const now = stamp();
  const items = live(await database.tasks.where('listId').equals(id).toArray());
  await writeRows(
    [
      ...items
        .sort((a, b) => Number(b.parentId !== null) - Number(a.parentId !== null))
        .map((row): RowChange => ({
          table: 'tasks',
          row: { ...row, updatedAt: now, deletedAt: now },
          kind: 'delete',
        })),
      { table: 'task_lists', row: { ...list, updatedAt: now, deletedAt: now }, kind: 'delete' },
    ],
    database,
  );
}

export interface TaskDraft {
  title: string;
  note: string | null;
  dueDate: string | null;
  dueTime: string | null;
  repeat: Repeat | null;
  assigneeId: string | null;
  parentId: string | null;
  /** Nur Erwachsene setzen ihn; fehlt er, bleibt der gespeicherte Betrag. */
  rewardCents?: number | null;
  /** Sterne fürs Erledigen (26.10.2026) — wie der Betrag nur von Erwachsenen. */
  rewardStars?: number | null;
}

export async function addTask(
  listId: string,
  draft: TaskDraft,
  database: KraftwerkDatabase = db,
): Promise<LocalTask | null> {
  const list = await database.task_lists.get(listId);
  if (!list || list.deletedAt) return null;
  const siblings = live(await database.tasks.where('listId').equals(listId).toArray());
  return writeRow(
    'tasks',
    {
      id: newId(),
      householdId: list.householdId,
      listId,
      parentId: draft.parentId,
      title: draft.title.trim(),
      note: draft.note?.trim() || null,
      dueDate: draft.dueDate,
      dueTime: draft.dueDate ? draft.dueTime : null,
      repeat: draft.dueDate ? draft.repeat : null,
      assigneeId: list.householdId ? draft.assigneeId : null,
      ...(list.householdId && draft.rewardCents ? { rewardCents: draft.rewardCents } : {}),
      ...(list.householdId && draft.rewardStars ? { rewardStars: draft.rewardStars } : {}),
      doneAt: null,
      doneBy: null,
      position: siblings.length,
      updatedAt: stamp(),
      deletedAt: null,
    },
    'upsert',
    database,
  );
}

export async function updateTask(
  id: string,
  draft: Partial<TaskDraft>,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const row = await database.tasks.get(id);
  if (!row || row.deletedAt) return;
  const next = { ...row, ...draft };
  if (!next.dueDate) {
    next.dueTime = null;
    next.repeat = null;
  }
  if (!row.householdId) next.assigneeId = null;
  await writeRow(
    'tasks',
    { ...next, title: next.title.trim(), updatedAt: stamp() },
    'upsert',
    database,
  );
}

/**
 * Löschen — mit Unteraufgaben. Gibt zurück, was gelöscht wurde, damit
 * „Rückgängig“ genau das wiederherstellen kann (`restoreTasks`).
 */
export async function deleteTask(
  id: string,
  database: KraftwerkDatabase = db,
): Promise<LocalTask[]> {
  const row = await database.tasks.get(id);
  if (!row || row.deletedAt) return [];
  const now = stamp();
  const children = live(await database.tasks.where('listId').equals(row.listId).toArray()).filter(
    (child) => child.parentId === id,
  );
  await writeRows(
    [
      ...children.map((child): RowChange => ({
        table: 'tasks',
        row: { ...child, updatedAt: now, deletedAt: now },
        kind: 'delete',
      })),
      { table: 'tasks', row: { ...row, updatedAt: now, deletedAt: now }, kind: 'delete' },
    ],
    database,
  );
  return [...children, row];
}

/** „Rückgängig“ nach dem Löschen: dieselben Zeilen wieder lebendig. */
export async function restoreTasks(
  rows: readonly LocalTask[],
  database: KraftwerkDatabase = db,
): Promise<void> {
  if (rows.length === 0) return;
  const now = stamp();
  // Die Aufgabe vor ihren Unteraufgaben — die zeigen auf sie.
  const ordered = [...rows].sort(
    (a, b) => Number(a.parentId !== null) - Number(b.parentId !== null),
  );
  await writeRows(
    ordered.map((task): RowChange => ({
      table: 'tasks',
      row: { ...task, deletedAt: null, updatedAt: now },
    })),
    database,
  );
}

/**
 * Abhaken.
 *
 * - Checkliste: Punkt abgehakt (zurückgesetzt wird beim Abschluss des Durchgangs).
 * - Wiederkehrend: Eintrag in die Vergangenheit, Tag rückt weiter — offen bleibt sie.
 * - Sonst: erledigt, mit Eintrag in die Vergangenheit.
 */
export async function completeTask(
  id: string,
  context: {
    me: string | null;
    today: string;
    /**
     * Wer erledigt hat, wenn es nicht ich bin — die Familienanzeige hakt für
     * die zuständige Person ab (Mia tippt am Küchentablet der Eltern). Der
     * Server nimmt nur Mitglieder des Haushalts an.
     */
    doneBy?: string | null;
    /**
     * Familienanzeige mit „zählt nicht für Belohnungen“: Der Erledigt-Eintrag
     * trägt `noReward`, der Server bucht dafür weder Geld noch Sterne.
     */
    noReward?: boolean;
  },
  database: KraftwerkDatabase = db,
): Promise<void> {
  const [row, all] = await Promise.all([database.tasks.get(id), database.task_lists.toArray()]);
  if (!row || row.deletedAt || row.doneAt) return;
  const list = all.find((entry) => entry.id === row.listId);
  const now = stamp();
  const doneBy = context.doneBy ?? context.me;

  if (list?.kind === 'checklist') {
    await writeRow('tasks', { ...row, doneAt: now, doneBy, updatedAt: now }, 'upsert', database);
    return;
  }

  const completion: LocalTaskCompletion = {
    id: newId(),
    householdId: row.householdId,
    listId: row.listId,
    taskId: row.id,
    title: row.title,
    dueDate: row.dueDate,
    doneAt: now,
    doneBy,
    ...(context.noReward ? { noReward: true } : {}),
    itemsDone: null,
    itemsTotal: null,
    updatedAt: now,
    deletedAt: null,
  };
  const updated: LocalTask =
    row.repeat && row.dueDate
      ? { ...row, dueDate: nextDueDate(row.dueDate, row.repeat, context.today), updatedAt: now }
      : { ...row, doneAt: now, doneBy, updatedAt: now };
  await writeRows(
    [
      { table: 'tasks', row: updated },
      { table: 'task_completions', row: completion },
    ],
    database,
  );
}

/** Versehentlich abgehakt: wieder offen — der Eintrag in der Vergangenheit bleibt gelöscht stehen. */
export async function reopenTask(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const row = await database.tasks.get(id);
  if (!row || row.deletedAt || !row.doneAt) return;
  const now = stamp();
  const completions = live(
    await database.task_completions.where('listId').equals(row.listId).toArray(),
  ).filter((entry) => entry.taskId === id && entry.doneAt === row.doneAt);
  await writeRows(
    [
      { table: 'tasks', row: { ...row, doneAt: null, doneBy: null, updatedAt: now } },
      ...completions.map((entry): RowChange => ({
        table: 'task_completions',
        row: { ...entry, updatedAt: now, deletedAt: now },
        kind: 'delete',
      })),
    ],
    database,
  );
}

/**
 * Checklisten-Durchgang abschließen: ein Eintrag mit „x von y", dann alle
 * Punkte wieder offen — die Vorlage ist bereit fürs nächste Mal.
 */
export async function finishChecklistRun(
  listId: string,
  me: string | null,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const list = await database.task_lists.get(listId);
  if (!list || list.deletedAt || list.kind !== 'checklist') return;
  const items = live(await database.tasks.where('listId').equals(listId).toArray());
  const now = stamp();
  await writeRows(
    [
      {
        table: 'task_completions',
        row: {
          id: newId(),
          householdId: list.householdId,
          listId,
          taskId: null,
          title: list.name,
          dueDate: null,
          doneAt: now,
          doneBy: me,
          itemsDone: items.filter((item) => item.doneAt).length,
          itemsTotal: items.length,
          updatedAt: now,
          deletedAt: null,
        },
      },
      ...items
        .filter((item) => item.doneAt)
        .map((item): RowChange => ({
          table: 'tasks',
          row: { ...item, doneAt: null, doneBy: null, updatedAt: now },
        })),
    ],
    database,
  );
}

export interface TaskView extends LocalTask {
  state: TaskState;
  children: (LocalTask & { state: TaskState })[];
}

/** Aufgaben einer Liste: Oberaufgaben mit ihren Unterpunkten, offene vor erledigten. */
export async function tasksOfList(
  listId: string,
  today: string,
  database: KraftwerkDatabase = db,
): Promise<TaskView[]> {
  const rows = live(await database.tasks.where('listId').equals(listId).toArray());
  const order = (a: LocalTask, b: LocalTask) =>
    Number(a.doneAt !== null) - Number(b.doneAt !== null) ||
    (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999') ||
    a.position - b.position;
  return rows
    .filter((row) => !row.parentId)
    .sort(order)
    .map((row) => ({
      ...row,
      state: taskState(row, today),
      children: rows
        .filter((child) => child.parentId === row.id)
        .sort(order)
        .map((child) => ({ ...child, state: taskState(child, today) })),
    }));
}

export interface DueTask extends LocalTask {
  state: TaskState;
  listName: string;
}

/**
 * Was mich betrifft und einen Tag hat — für Heute (überfällig + heute) und den
 * Kalender (alles im Fenster). „Mich" heißt: mir zugewiesen, oder von mir
 * angelegt und niemandem zugewiesen.
 */
export async function datedTasks(
  context: { me: string | null; today: string; from?: string; to?: string },
  database: KraftwerkDatabase = db,
): Promise<DueTask[]> {
  const [rows, lists] = await Promise.all([
    database.tasks.toArray(),
    database.task_lists.toArray(),
  ]);
  const nameOf = new Map(lists.filter((list) => !list.deletedAt).map((list) => [list.id, list]));
  return live(rows)
    .filter((row) => {
      const list = nameOf.get(row.listId);
      if (!list || list.kind === 'checklist' || !row.dueDate || row.doneAt) return false;
      const mine = row.assigneeId
        ? row.assigneeId === context.me
        : row.userId === undefined || row.userId === context.me;
      if (!mine) return false;
      if (context.from && row.dueDate < context.from) return false;
      if (context.to && row.dueDate > context.to) return false;
      return true;
    })
    .map((row) => ({
      ...row,
      state: taskState(row, context.today),
      listName: nameOf.get(row.listId)!.name,
    }))
    .sort(
      (a, b) =>
        a.dueDate!.localeCompare(b.dueDate!) || (a.dueTime ?? '').localeCompare(b.dueTime ?? ''),
    );
}

export async function completionsOf(
  listId: string | null,
  database: KraftwerkDatabase = db,
  limit = 100,
): Promise<LocalTaskCompletion[]> {
  const rows = listId
    ? await database.task_completions.where('listId').equals(listId).toArray()
    : await database.task_completions.toArray();
  return live(rows)
    .sort((a, b) => b.doneAt.localeCompare(a.doneAt))
    .slice(0, limit);
}

/**
 * Wer im Haushalt diese Woche was erledigt hat (E13) — eine Fairness-Anzeige,
 * keine Rangliste: gezählt werden Einträge in geteilten Listen, je Person.
 */
export async function householdDoneThisWeek(
  from: string,
  to: string,
  timeZone: string,
  database: KraftwerkDatabase = db,
): Promise<Map<string, number>> {
  const rows = await database.task_completions.toArray();
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (row.deletedAt || !row.householdId || !row.doneBy) continue;
    const day = isoDateInZone(new Date(row.doneAt), timeZone);
    if (day < from || day > to) continue;
    counts.set(row.doneBy, (counts.get(row.doneBy) ?? 0) + 1);
  }
  return counts;
}
